import React from "react";
import { FaTrophy } from "react-icons/fa";
import { TailSpin } from "react-loader-spinner";

import type {
  InviteCodeTopInviterApi,
  InviteCodesMetricsPayload,
} from "../../lib/api/inviteCodes";

type Props = {
  metrics: InviteCodesMetricsPayload | undefined;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

function formatRate(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n)}%`;
}

function formatAvgDays(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n)} days`;
}

function inviterName(inv: InviteCodeTopInviterApi): string {
  const name = [inv.firstName, inv.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  return inv.email || inv.userId || "—";
}

const METRIC_ROWS: {
  key: keyof Pick<
    InviteCodesMetricsPayload,
    | "redemptionRate"
    | "depositCompletionRate"
    | "tradeCompletionRate"
    | "avgDaysToComplete"
  >;
  label: string;
  format: (n: number | null | undefined) => string;
}[] = [
  {
    key: "redemptionRate",
    label: "Redemption Rate",
    format: formatRate,
  },
  {
    key: "depositCompletionRate",
    label: "Deposit Completion",
    format: formatRate,
  },
  {
    key: "tradeCompletionRate",
    label: "Trade Completion",
    format: formatRate,
  },
  {
    key: "avgDaysToComplete",
    label: "Avg. Time to Complete",
    format: formatAvgDays,
  },
];

const InviteCodeAnalyticsPanel: React.FC<Props> = ({
  metrics,
  isLoading,
  isError,
  onRetry,
}) => {
  const topInviters = metrics?.topInviters ?? [];

  return (
    <section
      className="rounded-2xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#181D22] p-5 shadow-[0_8px_32px_rgba(0,0,0,0.28)] sm:p-6"
      aria-labelledby="invite-analytics-heading"
      aria-busy={isLoading || undefined}
    >
      <h2
        id="invite-analytics-heading"
        className="font-sora text-[15px] font-semibold text-white"
      >
        Invite Code Analytics
      </h2>

      {isError ? (
        <div className="mt-4 flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
          <span>Could not load metrics.</span>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : isLoading && !metrics ? (
        <div className="mt-6 flex justify-center py-10">
          <TailSpin color="#0B75C2" height={32} width={32} />
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
            {METRIC_ROWS.map((m) => (
              <div
                key={m.key}
                className="rounded-xl border border-[#2C363E] bg-[#141920]/90 px-4 py-3"
              >
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#869AA9]">
                  {m.label}
                </p>
                <p className="mt-2 font-sora text-xl font-bold tabular-nums text-white">
                  {m.format(metrics?.[m.key])}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <div className="mb-3 flex items-center gap-2">
              <FaTrophy className="text-amber-400" aria-hidden />
              <h3 className="text-[13px] font-semibold text-[#E7EBEE]">
                Top Inviters by Successful Referrals
              </h3>
            </div>
            {topInviters.length === 0 ? (
              <p className="rounded-lg border border-dashed border-[#2C363E] px-4 py-6 text-center text-[13px] text-[#869AA9]">
                No inviter data yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {topInviters.map((inv, i) => (
                  <li
                    key={inv.userId || String(i)}
                    className="flex items-center gap-3 rounded-xl border border-[#2C363E] bg-[#141920]/80 px-4 py-3"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1C2227] text-[12px] font-bold text-[#AEBCC6]">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-semibold text-white">
                        {inviterName(inv)}
                      </p>
                      <p className="text-[12px] text-[#869AA9]">
                        {inv.referrals} referral
                        {inv.referrals === 1 ? "" : "s"}
                        {inv.totalMembers != null
                          ? ` · ${inv.totalMembers} member${
                              inv.totalMembers === 1 ? "" : "s"
                            }`
                          : ""}
                      </p>
                    </div>
                    <p className="shrink-0 text-[12px] font-medium text-[#AEBCC6]">
                      {(inv.pointsDistributed ?? 0).toLocaleString()} pts
                      distributed
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default InviteCodeAnalyticsPanel;
